import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import AssignmentIcon from '@material-ui/icons/AssignmentInd';
import React, { Component } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { UserConsumer } from '../../core/providers/UserProvider';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import { globalStyles } from '../style/gstheme';
import { makeCancelable, sortByProp } from '../utils/utils';
import InviteUserDialog from './InviteUserDialog';
import RecentUserCard from './RecentUserCard';
import UserCard from './UserCard';
import UserDetail from './UserDetail';

const styles = () => {
  return Object.assign({
    invitedauth: {
      padding: 12,
      marginBottom: 12,
      backgroundColor: '#FF6F00',
      borderRadius: 4,
      color: 'white'
    }
  }, globalStyles);
};

class UsersManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      selectedUser: null,
      isLoading: true,
      users: [],
      withdrawnInvitations: [],
      withdrawnUsernames: [],
      invitations: [],
      invitedUsers: [],
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.getUsers();
  }

  getUsers = async () => {
    this.setState(
      { isLoading: true },
      () => {
        this.doGetInvitees()
          .promise
          .then(data => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (data) {
              const invitations = data.invitations;
              const users = sortByProp([...data.users], 'familyName', 'givenName');
              this.setState({ users, invitations, isLoading: false });
            }
            else {
              this.setState({
                isLoading: false,
                page: 1,
                noneFound: true,
                noResults: false
              });
            }
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      });
  };

  doGetInvitees() {
    this.cancellable = makeCancelable(GS.invitees());
    return this.cancellable;
  }

  doCreateInvitation(username) {
    this.cancellable = makeCancelable(GS.createInvitation(username));
    return this.cancellable;
  }

  doWithdrawInvitation(id) {
    this.cancellable = makeCancelable(GS.withdrawInvitation(id));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  selectUser = (id) => this.setState({ selectedUser: id });

  inviteUser = async (username) => {
    try {
      await this.doCreateInvitation(username).promise;
      const invitedUsers = [...this.state.invitedUsers, username];
      this.setState({ invitedUsers });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  deleteUser = async () => {
    try {
      const user = this.state.selectedUser;
      const invitation = this.state.invitations.find(invitation => invitation.invitedUser === user);
      await this.doWithdrawInvitation(invitation.id).promise;
      const updatedWithdrawnInvitations = [...this.state.withdrawnInvitations, invitation.id];
      const withdrawnUsername = this.state.users.find(user => user.id === invitation.invitedUser).username;
      const updatedWithdrawnUsernames = [...this.state.withdrawnUsernames, withdrawnUsername];
      this.setState({ withdrawnInvitations: updatedWithdrawnInvitations, withdrawnUsernames: updatedWithdrawnUsernames });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  render() {

    const { classes } = this.props;
    const { isErrored, selectedUser, users, invitations, isLoading, withdrawnInvitations, withdrawnUsernames, invitedUsers } = this.state;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading) return <Loading />;

    const userToEdit = users.find(user => user.id === selectedUser);
    const invitation = invitations.find(invitation => invitation.invitedUser === selectedUser);

    return (
      <UserConsumer>
        {({ subscriptions, activeSubscription, invitations: userInvitations, user }) => {
          const accessedSubscriptions = [...subscriptions, ...userInvitations];
          const subscription = accessedSubscriptions && accessedSubscriptions.find(resource => {
            if (resource.type === "Subscription" && resource.id === activeSubscription) return true;
            if (resource.type === "Invitation" && resource.subscription === activeSubscription) return true;
            return false;
          });
          const isSubscriptionOwner = subscriptions ? subscriptions.some(resource => resource.id === activeSubscription) : false;
          // For backwards compatibility with subscription versions less than 1
          if (!subscription ||
            (!isSubscriptionOwner && !subscription.hasOwnProperty("subscriptionVersion")) ||
            (!isSubscriptionOwner && subscription.hasOwnProperty("subscriptionVersion") && subscription.subscriptionVersion < 1)) {
            return <ActionText offset="130px">Currently only subscription owners can manage users.</ActionText>;
          }
          const maxUsers = subscription ? subscription.maxUsers : 1;
          return <div>
            <div className={classes.controlPanel}>
              <Tooltip title="Number of users in subscription against maximum number permitted (owner counts as an extra user)">
                <Typography data-test="user-count" variant="body2" style={{ marginTop: 6 }}><AssignmentIcon style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }} /> {invitedUsers.length + users.length + 1}/{maxUsers}</Typography>
              </Tooltip>
              <div className={classes.tree} style={{ top: '120px' }}>
                {users.length === 0 && invitedUsers.length === 0 ?
                  <ActionText offset="130px">No users.</ActionText>
                  :
                  <div data-test="users-list">
                    {invitedUsers.length > 0 &&
                      <div data-test="recently-invited-users">
                        <Typography className={classes.subHeading} style={{ color: '#FF6F00', marginLeft: 10 }}>Recently Invited Users</Typography>
                        {invitedUsers.map((username) => <RecentUserCard key={username} username={username} />)}
                      </div>
                    }
                    {users.map((userToDisplay) => {
                      const userInvitation = invitations.find(invitation => invitation.invitedUser === userToDisplay.id);
                      if (userInvitation) {
                        return <UserCard
                          key={userToDisplay.id}
                          isSelected={userToDisplay.id === selectedUser}
                          invitation={userInvitation}
                          withDrawn={withdrawnInvitations.includes(userInvitation.id)}
                          user={userToDisplay}
                          onSelectUser={this.selectUser} />;
                      }
                      else return null;
                    })}
                  </div>
                }
              </div>
            </div>
            <div className={classes.dashboardPanel}>
              <div className={classes.dashboardAdd}>
                {invitedUsers.length + users.length + 1 < maxUsers
                  && (isSubscriptionOwner || subscription.subscriptionVersion >= 1)
                  && <InviteUserDialog
                    existingUsers={users}
                    invitedUsers={invitedUsers}
                    withdrawnUsernames={withdrawnUsernames}
                    onInviteUser={this.inviteUser}
                  />}
              </div>
              {selectedUser ?
                <UserDetail
                  key={selectedUser}
                  invitation={invitation}
                  user={userToEdit}
                  withDrawn={withdrawnInvitations.includes(invitation.id)}
                  graph={activeSubscription}
                  id={selectedUser}
                  readOnly={userToEdit.id === user.id}
                  onDeleteUser={this.deleteUser} />
                :
                (users.length > 0 ?
                  <ActionText offset="180px">Select a user to view their detail and edit their groups.</ActionText>
                  :
                  <ActionText offset="180px">No users are available to edit.</ActionText>
                )
              }
            </div>
          </div>;
        }}
      </UserConsumer>

    );
  }
}

UsersManager.propTypes = {
  classes: PropTypes.object
};

export default withStyles(styles)(UsersManager);